import { createRouter, createWebHistory } from 'vue-router'
import LandingPage from '../views/LandingPage.vue'
import LoginPage from '../views/LoginPage.vue'
import PasswordResetPage from '../views/PasswordResetPage.vue'
import ProfilePage from '../views/ProfilePage.vue'
import WhoPage from '../views/WhoPage.vue'
import PhotosPage from '../views/PhotosPage.vue'
import MessagePage from '../views/MessagePage.vue'
import ContactsPage from '../views/ContactsPage.vue'
import PreviewPage from '../views/PreviewPage.vue'
import SavePage from '../views/SavePage.vue'
import PaymentPage from '../views/PaymentPage.vue'
import SharePage from '../views/SharePage.vue'
import PublicMemorialPage from '../views/PublicMemorialPage.vue'
import MemorialLoadingPage from '../views/MemorialLoadingPage.vue'
import MemorialDashboardPage from '../views/MemorialDashboardPage.vue'
import EditMessagePage from '../views/EditMessagePage.vue'
import EditPhotosPage from '../views/EditPhotosPage.vue'
import EditStoriesPage from '../views/EditStoriesPage.vue'
import EditDetailsPage from '../views/EditDetailsPage.vue'
import SlideShowPage from '../views/SlideShowPage.vue'
import VideoSlideShowPage from '../views/VideoSlideShowPage.vue'
import EditContactsSharePage from '../views/EditContactsSharePage.vue'
import MemorialAdminsPage from '../views/MemorialAdminsPage.vue'
import AdminInviteAcceptPage from '../views/AdminInviteAcceptPage.vue'
import MemorialDonationsPage from '../views/MemorialDonationsPage.vue'
import FeedPage from '../views/FeedPage.vue'
import ForMemorialPage from '../views/ForMemorialPage.vue'
import ImageGridPage from '../views/ImageGridPage.vue'
import PrivacyPolicyPage from '../views/PrivacyPolicyPage.vue'
import TermsAndConditionsPage from '../views/TermsAndConditionsPage.vue'
import QrRedirectPage from '../views/QrRedirectPage.vue'
import GuidesIndexPage from '../views/GuidesIndexPage.vue'
import GuidePage from '../views/GuidePage.vue'

const routes = [
  {
    path: '/',
    name: 'Landing',
    component: LandingPage
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage
  },
  {
    path: '/password/reset/:code',
    name: 'PasswordReset',
    component: PasswordResetPage,
    props: true
  },
  {
    path: '/profile',
    name: 'Profile',
    component: ProfilePage
  },
  {
    path: '/who',
    name: 'Who',
    component: WhoPage
  },
  {
    path: '/photos',
    name: 'Photos',
    component: PhotosPage
  },
  {
    path: '/message',
    name: 'Message',
    component: MessagePage
  },
  {
    path: '/contacts',
    name: 'Contacts',
    component: ContactsPage
  },
  {
    path: '/preview',
    name: 'Preview',
    component: PreviewPage
  },
  {
    path: '/save',
    name: 'Save',
    component: SavePage
  },
  {
    path: '/payment',
    name: 'Payment',
    component: PaymentPage
  },
  {
    path: '/share',
    name: 'Share',
    component: SharePage
  },
  {
    path: '/view/:slug',
    name: 'PublicMemorial',
    component: PublicMemorialPage,
    props: true
  },
  {
    path: '/for/:slug',
    name: 'ForMemorial',
    component: ForMemorialPage,
    props: true
  },
  {
    path: '/donate/:slug',
    name: 'MemorialDonations',
    component: MemorialDonationsPage,
    props: true
  },
  {
    path: '/memory/load/:uuid',
    name: 'MemorialLoading',
    component: MemorialLoadingPage,
    props: true
  },
  {
    path: '/memory/dashboard',
    name: 'MemorialDashboard',
    component: MemorialDashboardPage
  },
  {
    path: '/memory/edit-message',
    name: 'EditMessage',
    component: EditMessagePage
  },
  {
    path: '/memory/edit-photos',
    name: 'EditPhotos',
    component: EditPhotosPage
  },
  {
    path: '/memory/edit-stories',
    name: 'EditStories',
    component: EditStoriesPage
  },
  {
    path: '/memory/edit-details',
    name: 'EditDetails',
    component: EditDetailsPage
  },
  {
    path: '/memory/slideshow',
    name: 'SlideShow',
    component: SlideShowPage
  },
  {
    path: '/memory/video-slideshow',
    name: 'VideoSlideShow',
    component: VideoSlideShowPage
  },
  {
    path: '/memory/edit-contacts-share',
    name: 'EditContactsShare',
    component: EditContactsSharePage
  },
  {
    path: '/memory/edit-donations',
    name: 'EditDonations',
    redirect: { name: 'MemorialDashboard' }
  },
  {
    path: '/memory/administrators',
    name: 'MemorialAdmins',
    component: MemorialAdminsPage
  },
  {
    path: '/admin-invite/:token',
    name: 'AdminInviteAccept',
    component: AdminInviteAcceptPage,
    props: true
  },
  {
    path: '/memory/feed',
    name: 'Feed',
    component: FeedPage
  },
  {
    path: '/dev/image-grid',
    name: 'ImageGrid',
    component: ImageGridPage
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicyPage
  },
  {
    path: '/terms-and-conditions',
    name: 'TermsAndConditions',
    component: TermsAndConditionsPage
  },
  {
    path: '/guides',
    name: 'Guides',
    component: GuidesIndexPage,
    meta: { guides: true }
  },
  {
    path: '/guides/category/:category',
    name: 'GuidesCategory',
    component: GuidesIndexPage,
    meta: { guides: true }
  },
  {
    path: '/guides/tag/:tag',
    name: 'GuidesTag',
    component: GuidesIndexPage,
    meta: { guides: true }
  },
  {
    path: '/guides/:slug',
    name: 'Guide',
    component: GuidePage,
    props: true,
    meta: { guides: true }
  },
  {
    path: '/qr/:token',
    name: 'QrRedirect',
    component: QrRedirectPage,
    props: true
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guard to check for unsaved changes
router.beforeEach(async (to, from, next) => {
  // Import store dynamically to avoid circular imports
  const { useMemorialStore } = await import('../stores/memorial')
  const store = useMemorialStore()

  // Page mapping for tracking
  const formPages = ['/who', '/photos', '/message', '/contacts', '/preview', '/save']
  const pageMap = {
    '/who': 'who',
    '/photos': 'photos',
    '/message': 'message',
    '/contacts': 'contacts'
    // Preview page removed - it's read-only, no change tracking needed
  }

  // Only check for unsaved changes if user is logged in, has a saved memorial, and is navigating between form pages
  // First-time users creating a memorial are never shown the unsaved changes modal
  // Don't show modal for users creating a new memorial (even if logged in)
  const isEditingExistingMemorial = store.isAccountCreated &&
                                     store.isEmailVerified &&
                                     store.apiToken &&
                                     store.memorialUuid &&
                                     store.originalMemorialData !== null

  if (isEditingExistingMemorial) {

    // Check if we're navigating away from a form page and have page-specific changes
    if (formPages.includes(from.path) && to.path !== from.path) {
      const fromPageName = pageMap[from.path]
      const hasChangesOnThisPage = fromPageName && store.hasPageChanges(fromPageName)

      if (hasChangesOnThisPage) {

        // Show custom modal and wait for user decision
        try {
          const userConfirmed = await store.showNavigationModal(() => {
            // This function will be called if user confirms navigation
            next()
          })

          if (userConfirmed) {
            // User wants to leave, proceed with navigation
            // Set up page tracking for the destination page
            const toPageName = pageMap[to.path]
            if (toPageName) {
              store.enterPage(toPageName)
            }
            next()
          } else {
            // User wants to stay, cancel navigation
            next(false)
          }
        } catch (error) {
          console.error('Navigation modal error:', error)
          // Fallback to allowing navigation
          next()
        }

        return // Important: don't call next() again
      }
    }
  }

  // Set up page tracking for destination page (only for editing existing memorials)
  const toPageName = pageMap[to.path]
  if (toPageName && isEditingExistingMemorial) {
    store.enterPage(toPageName)
  }

  // Proceed with navigation if no unsaved changes
  next()
})

export default router
